// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { EducBox, ResultCard, fmt } from './shared';
import { toast } from 'sonner';

const SERVICES = [
  { id: 'netflix', name: 'Netflix', price: 13.99, cat: 'Streaming Video' },
  { id: 'disney', name: 'Disney+', price: 8.99, cat: 'Streaming Video' },
  { id: 'amazon', name: 'Amazon Prime', price: 4.99, cat: 'Streaming Video' },
  { id: 'appletv', name: 'Apple TV+', price: 9.99, cat: 'Streaming Video' },
  { id: 'now', name: 'NOW TV', price: 9.99, cat: 'Streaming Video' },
  { id: 'paramount', name: 'Paramount+', price: 7.99, cat: 'Streaming Video' },
  { id: 'spotify', name: 'Spotify', price: 10.99, cat: 'Musica' },
  { id: 'applemusic', name: 'Apple Music', price: 10.99, cat: 'Musica' },
  { id: 'ytpremium', name: 'YouTube Premium', price: 13.99, cat: 'Musica' },
  { id: 'dazn', name: 'DAZN', price: 40.99, cat: 'Sport' },
  { id: 'sky', name: 'Sky Sport', price: 35.00, cat: 'Sport' },
  { id: 'ms365', name: 'Microsoft 365', price: 7.00, cat: 'Produttività' },
  { id: 'adobe', name: 'Adobe Creative', price: 60.00, cat: 'Produttività' },
  { id: 'gcloud', name: 'Google One', price: 2.99, cat: 'Produttività' },
  { id: 'icloud', name: 'iCloud+', price: 0.99, cat: 'Produttività' },
];

const CATS = [...new Set(SERVICES.map(s => s.cat))];

const TrackerAbbonamenti = () => {
  const [checked, setChecked] = useState({});
  const [custom, setCustom] = useState([]);
  const [newName, setNewName] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newFreq, setNewFreq] = useState('mensile');

  const toggle = (id) => setChecked(prev => ({ ...prev, [id]: !prev[id] }));

  const addCustom = () => {
    if (!newName || !newPrice) return;
    const priceMensile = newFreq === 'annuale' ? parseFloat(newPrice) / 12 : newFreq === 'settimanale' ? parseFloat(newPrice) * 4.33 : parseFloat(newPrice);
    setCustom(prev => [...prev, { id: `c${Date.now()}`, name: newName, price: priceMensile, original: parseFloat(newPrice), freq: newFreq }]);
    setNewName(''); setNewPrice('');
  };

  const removeCustom = (id) => setCustom(prev => prev.filter(c => c.id !== id));

  const activeServices = SERVICES.filter(s => checked[s.id]);
  const totalMensile = activeServices.reduce((s, sv) => s + sv.price, 0) + custom.reduce((s, c) => s + c.price, 0);
  const totalAnnuo = totalMensile * 12;
  const totalCount = activeServices.length + custom.length;

  const daRivalutare = [...activeServices, ...custom].filter(s => s.price >= 10).sort((a, b) => b.price - a.price);

  const top2Savings = daRivalutare.slice(0, 2).reduce((s, sv) => s + sv.price, 0);
  const pacProiezione = top2Savings > 0 ? Math.round(top2Savings * 12 * (Math.pow(1 + 0.07 / 12, 240) - 1) / (0.07 / 12)) : 0;

  const handleShare = async () => {
    const text = `Ho €${fmt(totalMensile)}/mese di abbonamenti 😱\nScopri i tuoi → finsapiens.it/calcolatori/tracker-abbonamenti`;
    if (navigator.share) {
      try { await navigator.share({ title: 'I miei abbonamenti - SAPIENS', text }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(text); toast.success('Testo copiato! ✓'); } catch {}
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <EducBox text="Il costo medio degli abbonamenti digitali per famiglia italiana nel 2026 è €120-180/anno. Spesso non ci rendiamo conto di quanti ne abbiamo attivi. Seleziona i tuoi e scopri il totale reale." />

      {/* Services list by category */}
      {CATS.map(cat => (
        <div key={cat} className="mb-4">
          <p className="text-xs font-semibold text-[#888780] uppercase tracking-wider mb-2">{cat}</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {SERVICES.filter(s => s.cat === cat).map(s => (
              <button key={s.id} onClick={() => toggle(s.id)} data-testid={`abbonamento-${s.id}`}
                className="flex items-center justify-between p-3 rounded-xl text-left transition-all"
                style={{ background: checked[s.id] ? 'rgba(61,122,82,0.12)' : '#252523', border: `1px solid ${checked[s.id] ? 'rgba(61,122,82,0.4)' : 'rgba(255,255,255,0.06)'}` }}>
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded flex items-center justify-center transition-all ${checked[s.id] ? 'bg-[#3D7A52]' : 'border-2 border-white/20'}`}>
                    {checked[s.id] && <span className="text-white text-xs font-bold">✓</span>}
                  </div>
                  <span className="text-sm text-[#E8E8E4]">{s.name}</span>
                </div>
                <span className="text-sm font-medium" style={{ color: checked[s.id] ? '#3D7A52' : '#888780' }}>€{s.price.toFixed(2)}/mese</span>
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Custom entries */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-[#888780] uppercase tracking-wider mb-2">Aggiungi altri abbonamenti</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nome abbonamento" className="flex-1 min-w-32 bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm" />
          <input type="number" value={newPrice} onChange={e => setNewPrice(e.target.value)} placeholder="Importo €" className="w-28 bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm" />
          <select value={newFreq} onChange={e => setNewFreq(e.target.value)} className="bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm">
            <option value="mensile">Mensile</option>
            <option value="annuale">Annuale</option>
            <option value="settimanale">Settimanale</option>
          </select>
          <button onClick={addCustom} className="px-4 py-2 rounded-xl text-sm font-medium transition-all sapiens-btn-primary" data-testid="add-custom-btn">+ Aggiungi</button>
        </div>
        {custom.map(c => (
          <div key={c.id} className="flex items-center justify-between p-3 rounded-xl mb-2" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)' }}>
            <div>
              <span className="text-sm text-[#E8E8E4]">{c.name}</span>
              <span className="text-xs text-[#888780] ml-2">€{c.original.toFixed(2)}/{c.freq}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-[#C9A84C]">€{c.price.toFixed(2)}/mese</span>
              <button onClick={() => removeCustom(c.id)} className="text-[#888780] hover:text-[#E24B4A] transition-colors text-lg">×</button>
            </div>
          </div>
        ))}
      </div>

      {/* Results */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4" data-testid="tracker-results">
        <ResultCard label="Spesa mensile totale" value={`€${fmt(totalMensile)}`} gold />
        <ResultCard label="Spesa annuale totale" value={`€${fmt(totalAnnuo)}`} />
        <ResultCard label="Abbonamenti attivi" value={String(totalCount)} />
        <ResultCard label="Da rivalutare (>€10/mese)" value={String(daRivalutare.length)} />
      </div>

      {daRivalutare.length > 0 && (
        <div className="sapiens-card p-4 mb-4">
          <p className="text-sm font-semibold text-[#C9A84C] mb-2">Abbonamenti che potresti rivalutare</p>
          <div className="space-y-2">
            {daRivalutare.map(s => (
              <div key={s.id} className="flex justify-between items-center py-1.5 border-b border-white/[0.05]">
                <span className="text-sm text-[#E8E8E4]">{s.name}</span>
                <div className="text-right">
                  <span className="text-sm font-medium text-[#C9A84C]">€{s.price.toFixed(2)}/mese</span>
                  <p className="text-xs text-[#888780]">Stai usando questo abbonamento regolarmente?</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {top2Savings > 0 && (
        <div className="p-4 rounded-xl mb-4" style={{ background: 'rgba(61,122,82,0.08)', border: '1px solid rgba(61,122,82,0.25)' }}>
          <p className="text-sm text-[#3D7A52] font-medium mb-1">Suggerimento risparmio</p>
          <p className="text-sm text-[#888780]">
            Cancellando i 2 abbonamenti più costosi potresti risparmiare <strong className="text-[#E8E8E4]">€{fmt(top2Savings * 12)}/anno</strong> —
            che investiti in PAC al 7% per 20 anni diventano{' '}
            <strong className="text-[#3D7A52]">€{fmt(pacProiezione)}</strong>.{' '}
            <a href="/calcolatori/pac" className="underline text-[#C9A84C]">Calcola con il PAC →</a>
          </p>
        </div>
      )}

      <button onClick={handleShare} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all" style={{ border: '1px solid rgba(201,168,76,0.4)', color: '#C9A84C', background: 'rgba(201,168,76,0.06)' }} data-testid="share-abbonamenti-btn">
        Condividi il risultato
      </button>
</motion.div>
  );
};

export default TrackerAbbonamenti;
